import {
  EBranchMutationType,
  EMediaSequenceState,
  ETranscriptionState,
  MediaSequenceResponse,
  SubtitleProfileResponse,
} from 'api/core';
import {
  useGetMediaSequence,
  useInvokeMediaSequence,
  useRedoMediaSequence,
} from 'api/useMediaSequencesApi';
import { MutateEntity } from 'components/Form/MutateEntity';
import { MediaSequenceTranscriptionForm } from 'pages/MediaOrbit/components/MediaSequence/Transcription/MediaSequenceTranscriptionForm';
import { useCallback, useEffect, useState } from 'react';
import { useMediaSequenceSetup } from './MediaSequenceSetupProvider';
import { MediaSequenceProgress } from './MediaSequenceProgress';
import { isTemplate } from 'pages/MediaOrbit/media-sequence-utils';
import {
  useCreateMediaSequenceBranchMutation,
  useDeleteMediaSequenceBranchMutation,
  useUpdateMediaSequenceBranchMutation,
} from 'api/useMediaSequenceBranchesApi';
import { useGetSubtitleProfiles } from 'api/useSubtitleProfilesApi';
import { buildQueryStateGetAll } from 'components/Table/useTableQueryState';
import { LabelWithHelperText } from 'components/Form/LabelWithHelperText';
import { EntitySelectSingle } from 'components/Select/EntitySelectSingle';
import { SubtitlePreview } from 'pages/MediaOrbit/components/MediaSequenceBranchMutation/Types/BakeSubtitles';

interface MediaSequenceSetupTranscriptionProps {
  mediaSequence: MediaSequenceResponse;
  disabled: boolean;
}

export const MediaSequenceSetupTranscription = ({
  mediaSequence,
  disabled,
}: MediaSequenceSetupTranscriptionProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const { data } = useGetMediaSequence(mediaSequence.id, {
    refetchInterval:
      mediaSequence.transcriptionState === ETranscriptionState.Pending
        ? 5000
        : undefined,
  });

  if (data?.transcription)
    data.transcription = data.transcription
      .replaceAll('\r\n', '\n')
      .replaceAll('\r', '\n');

  const { handlePrevious, handleNext } = useMediaSequenceSetup();

  const [disableNext, setDisableNext] = useState<boolean>(false);
  const [disableText, setDisableText] = useState<string>('');

  const onRevalidateStep = useCallback(() => {
    if (isTemplate(mediaSequence.type)) {
      setDisableNext(true);
      setDisableText('Kan ikke lave undertekster på en skabelon');
    } else if (
      mediaSequence.transcriptionState !== ETranscriptionState.Approved
    ) {
      setDisableNext(true);
      if (
        mediaSequence.transcriptionState === ETranscriptionState.NotApplicable
      ) {
        setDisableText('Lav undertekster for at forsætte');
      } else {
        setDisableText('Godkend underteksterne for at fortsætte');
      }
    } else {
      setDisableNext(false);
      setDisableText('');
    }
  }, [mediaSequence.transcriptionState, mediaSequence.type]);

  useEffect(() => {
    onRevalidateStep();
  }, [mediaSequence, onRevalidateStep]);

  if (!data) return null;

  return (
    <>
      {/* If the video is a template, we can't generate subtitles */}
      {isTemplate(data) ? (
        <div className="space-y-4">
          <p>
            Du kan ikke lave undertekster på en skabelon. Hvis du vil lave
            undertekster, så lav en kopi af videoen og prøv igen.
          </p>
        </div>
      ) : null}

      {/* If the video has failed, we can't generate subtitles */}
      {data.state === EMediaSequenceState.Failed ? (
        <div className="space-y-4">
          <p>
            Videoen er fejlet og kan ikke lave undertekster. Der er oprettet en
            supportsag
          </p>
        </div>
      ) : null}

      {/* First step: Start flow */}
      {data.transcriptionState === ETranscriptionState.NotApplicable &&
      !isTemplate(data) &&
      data.state != EMediaSequenceState.Failed ? (
        <TranscriptionInitiateFlow
          isLoading={isLoading}
          disabled={disabled}
          data={data}
          setIsLoading={setIsLoading}
        />
      ) : null}

      {/* Second step: Wait for transcription */}
      {data.transcriptionState === ETranscriptionState.Pending ? (
        <MediaSequenceProgress mediaSequence={mediaSequence} />
      ) : null}

      {/* Third step: Edit and approve, but video still processing */}
      {data.state === EMediaSequenceState.Processing &&
      (data.transcriptionState === ETranscriptionState.Approved ||
        data.transcriptionState === ETranscriptionState.Generated) ? (
        <div className="space-y-4">
          <p>
            Underteksterne er nu blevet lavet af Smart Video, og du kan ændre i
            dem. Når du er tilfreds med resultatet, kan du godkende dem, og de
            vil blive låst.
          </p>
          {data.transcription === '' ? (
            <p>
              Resultatet er tomt. Der er ingen undertekster at redigere eller
              godkende. Hvis der skulle have været undertekster, så lav en kopi
              af videoen og prøv igen.
            </p>
          ) : null}
          {/* TODO: Maybe a permission? */}
          <div className="space-y-2">
            <p>
              Hvis du ikke er tilfreds med den video eller undertekster der er
              blevet lavet, så kan du låse op for videoen og lave de nødvendige
              ændringer eller køre processen igen.
            </p>
            <MutateEntity
              trigger={
                <button type="button" className="btn btn-warning">
                  Lav video om
                </button>
              }
              title={`Lav videoen ${mediaSequence.name} om?`}
              description={`Er du sikker på du vil lave videoen '${mediaSequence.name}' om? Din opsætning vil blive gemt men resultaterne vil blive slettet.`}
              setter={useRedoMediaSequence}
              mutationVariables={{ id: mediaSequence.id }}
            />
          </div>
          <MediaSequenceTranscriptionForm
            mediaSequence={data}
            disabled={disabled}
          />
        </div>
      ) : null}

      {/* Fourth step: Video processed */}
      {data.transcriptionState == ETranscriptionState.Approved &&
      (data.state === EMediaSequenceState.Processed ||
        data.state === EMediaSequenceState.Published) ? (
        <div className="space-y-4">
          {data.transcription ? (
            <>
              <p>
                Underteksterne er blevet genereret og godkendt. Nedenfor kan du
                se underteksterne der blev anvendt i videoen.
              </p>
              <MediaSequenceTranscriptionForm
                mediaSequence={data}
                disabled={disabled}
              />
            </>
          ) : (
            <p>Der blev ikke lavet undertekster til denne video.</p>
          )}
        </div>
      ) : null}

      <div className="flex flex-col mt-4 mb-8">
        <div id="step-navigation" className="join justify-center mt-2">
          <button className="join-item btn" onClick={handlePrevious}>
            Forrige trin
          </button>
          <button
            data-testid="media-sequence-next-step"
            className="join-item btn"
            onClick={handleNext}
            disabled={disableNext}
          >
            Næste trin
          </button>
        </div>
        {disableNext && disableText ? (
          <div className="text-center mt-2">
            <p className="text-sm text-gray-500">{disableText}</p>
          </div>
        ) : null}
      </div>
    </>
  );
};

const TranscriptionInitiateFlow = ({
  isLoading,
  disabled,
  data,
  setIsLoading,
}: {
  isLoading: boolean;
  disabled: boolean;
  data: MediaSequenceResponse;
  setIsLoading: (loading: boolean) => void;
}) => {
  const {
    mutateAsync: deleteMediaSequenceBranchMutationAsync,
    isPending: isPendingDelete,
  } = useDeleteMediaSequenceBranchMutation(false);

  const {
    mutateAsync: createMediaSequenceBranchMutationAsync,
    isPending: isPendingCreate,
  } = useCreateMediaSequenceBranchMutation(false);

  const {
    mutateAsync: updateMediaSequenceBranchMutationAsync,
    isPending: isPendingUpdate,
  } = useUpdateMediaSequenceBranchMutation(false);

  const { data: subtitleProfiles } = useGetSubtitleProfiles(
    buildQueryStateGetAll<SubtitleProfileResponse>()
  );

  const bakeSubtitlesMutation = data.branchMutations.find(
    (mutation) => mutation.type === EBranchMutationType.BakeSubtitles
  );

  const [isAnythingPending, setIsAnythingPending] = useState<boolean>();

  const { handleNext } = useMediaSequenceSetup();

  useEffect(() => {
    setIsAnythingPending(
      isPendingDelete || isPendingCreate || isPendingUpdate || isLoading
    );
  }, [isPendingDelete, isPendingCreate, isPendingUpdate, isLoading]);

  const onToggleBakeSubtitles = useCallback(async () => {
    if (isAnythingPending || disabled) return;

    if (!bakeSubtitlesMutation) {
      await createMediaSequenceBranchMutationAsync({
        id: data.id,
        mediaSequenceBranchMutationCreateRequest: {
          type: EBranchMutationType.BakeSubtitles,
          bakeSubtitles: {
            subtitleProfileId: subtitleProfiles?.data[0]?.id ?? null,
          },
        },
      });
    } else {
      await deleteMediaSequenceBranchMutationAsync({
        id: bakeSubtitlesMutation.id,
      });
    }
  }, [
    isAnythingPending,
    disabled,
    bakeSubtitlesMutation,
    createMediaSequenceBranchMutationAsync,
    deleteMediaSequenceBranchMutationAsync,
    data.id,
    subtitleProfiles?.data,
  ]);

  // When profile is selected, we must call updateMediaSequenceBranchMutationAsync
  const onSubtitleProfileSelected = useCallback(
    async (profile: SubtitleProfileResponse | null) => {
      if (!bakeSubtitlesMutation) return;

      await updateMediaSequenceBranchMutationAsync({
        id: bakeSubtitlesMutation.id,
        mediaSequenceBranchMutationUpdateRequest: {
          type: EBranchMutationType.BakeSubtitles,
          bakeSubtitles: {
            subtitleProfileId: profile?.id ?? null,
          },
        },
      });
    },
    [bakeSubtitlesMutation, updateMediaSequenceBranchMutationAsync]
  );

  if (!subtitleProfiles) return null;

  const activeSubtitleProfile =
    subtitleProfiles.data.find(
      (p) => p.id === bakeSubtitlesMutation?.bakeSubtitles?.subtitleProfileId
    ) || subtitleProfiles.data[0];

  return (
    <div className="flex flex-col xl:flex-row w-full space-y-4 xl:space-y-0 xl:space-x-8">
      {/* Left column: Settings */}
      <div className="flex-1 space-y-4">
        <h3 className="text-lg font-semibold mb-2">Undertekstopsætning</h3>
        <p>
          Når du trykker på knappen, starter Smart Video med at klippe videoen
          sammen og tilføje undertekster. Sørg for, at alt er klar, da du ikke
          kan ændre videoen bagefter.
        </p>
        <p>
          Du får mulighed for at rette og godkende underteksterne, før de bliver
          endelige.
        </p>

        <div className="flex flex-col items-start space-y-4 bg-base-200 p-4 rounded-sm border-gray-300 border">
          <div className="form-control flex-row items-center space-x-4">
            <label
              htmlFor="toggle-bake-subtitles"
              className="cursor-pointer label"
            >
              Inkluder undertekster
            </label>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              id="toggle-bake-subtitles"
              checked={!!bakeSubtitlesMutation}
              disabled={isAnythingPending || disabled}
              onChange={onToggleBakeSubtitles}
            />
          </div>

          {bakeSubtitlesMutation ? (
            <div className="form-control w-full">
              <LabelWithHelperText
                label="Undertekstprofil"
                helperText="Vælg en undertekstprofil for at styre udseendet af underteksterne. Hvis du ikke vælger en, vil standardprofilen blive brugt."
                inDialog={false}
              />
              <EntitySelectSingle<SubtitleProfileResponse>
                data={subtitleProfiles.data}
                renderFormat={(e) => <p>{e.name}</p>}
                onSelect={onSubtitleProfileSelected}
                initialValue={activeSubtitleProfile}
                searchPropertyKey="name"
                inDialog={false}
                disabled={
                  disabled || isAnythingPending || !bakeSubtitlesMutation
                }
              />
              <span className="label-text text-gray-500 mt-2">
                {activeSubtitleProfile.description}
              </span>
            </div>
          ) : null}

          <MutateEntity
            trigger={
              <button
                id="subtitles-generate-subtitles"
                data-testid="media-sequence-subtitles-generate"
                className="btn btn-primary"
                disabled={isAnythingPending}
              >
                {bakeSubtitlesMutation
                  ? 'Lav video med undertekster'
                  : 'Lav video uden undertekster'}
              </button>
            }
            title="Start video?"
            description={`Er du sikker på at påbegynde redigering af videoen '${data.name}'? Du kan ikke ændre i den efterfølgende.`}
            setter={useInvokeMediaSequence}
            mutationVariables={{ id: data.id }}
            onMutate={() => {
              setIsLoading(false);
              // If subtitles were disabled, we need to navigate to the next step
              if (!bakeSubtitlesMutation) {
                handleNext();
              }
            }}
            onMutateConfirm={() => setIsLoading(true)}
            disabled={isAnythingPending || disabled}
            hidden={disabled}
          />
        </div>
      </div>

      {/* Right column: Preview */}
      {bakeSubtitlesMutation ? (
        <div className="flex-1">
          <SubtitlePreview subtitleProfile={activeSubtitleProfile} />
        </div>
      ) : null}
    </div>
  );
};
